import { useLocation } from 'react-router-dom';

export type ViewKey = 'portfolio' | 'brand';
export type ViewScope = ViewKey | 'both';

export interface NavItemData {
    navItem: string;
    scope: ViewScope;
    style: string;
    hidden?: boolean;
    available: boolean;
}

export const VIEW_PATHS: Record<ViewKey, string> = {
    portfolio: '/portfolio',
    brand: '/brand',
};

export const VIEW_LABELS: Record<ViewKey, string> = {
    portfolio: 'Portfolio',
    brand: 'Brand',
};

const resumeStyle = 'py-1 px-3 border flex justify-center items-center border-Primary !text-Primary hover:bg-Primary hover:text-white rounded-full transition ease-in-out duration-250 cursor-pointer';

const hidden = '!hidden';
const item = '!flex';

const SECTIONS: NavItemData[] = [
    // Anchor for the logo / scroll-to-top. Not rendered in the nav.
    { navItem: 'Home', scope: 'both', style: hidden, hidden: true, available: true },

    // Recruiter facing
    { navItem: 'About Me', scope: 'portfolio', style: item, available: true },
    { navItem: 'Projects', scope: 'portfolio', style: item, available: true },
    { navItem: 'Skills', scope: 'portfolio', style: item, available: true },
    { navItem: 'My Resume', scope: 'portfolio', style: `!flex ${resumeStyle}`, available: true },

    // Client facing
    { navItem: 'Brand', scope: 'brand', style: item, available: true },
    { navItem: 'Packages', scope: 'brand', style: item, available: true },
    { navItem: 'Process', scope: 'brand', style: item, available: true },
    { navItem: 'Service', scope: 'brand', style: item, available: true },

    // Shared by both views
    { navItem: 'Contact', scope: 'both', style: item, available: true },
];

export const getViewFromPath = (pathname: string): ViewKey =>
    pathname === VIEW_PATHS.brand ? 'brand' : 'portfolio';

const useNavData = (view?: ViewKey) => {
    const { pathname } = useLocation();
    const currentView = view ?? getViewFromPath(pathname);
    const otherView: ViewKey = currentView === 'brand' ? 'portfolio' : 'brand';

    // "both" sections are rendered on every view, so they keep the current route.
    const sections = SECTIONS.filter(
        (section) => section.scope === 'both' || section.scope === currentView
    );

    const navItems = sections.map((section) => ({
        ...section,
        path: VIEW_PATHS[currentView],
    }));

    // First real section on this view, used by the hero call to action.
    const firstSection =
        navItems.find((navItem) => navItem.navItem !== 'Home')?.navItem ?? 'Home';

    const sectionIds = navItems.map((navItem) => navItem.navItem);

    return {
        navItems,
        currentView,
        otherView,
        currentPath: VIEW_PATHS[currentView],
        otherPath: VIEW_PATHS[otherView],
        firstSection,
        sectionIds,
    };
};

export default useNavData;
